// Account Dialog Component
import { AuthManager } from "../../services/auth-manager";
import { I18nManager } from "../../services/i18n-manager";
import { DialogManager } from "./dialog-manager";

export class AccountDialog {
  private authManager: AuthManager;
  private i18nManager: I18nManager;
  private dialogManager: DialogManager;
  private dialog: HTMLElement | null = null;
  private currentTab: string = "profile";

  constructor(
    authManager: AuthManager,
    i18nManager: I18nManager,
    dialogManager: DialogManager
  ) {
    this.authManager = authManager;
    this.i18nManager = i18nManager;
    this.dialogManager = dialogManager;
  }

  init(): void {
    this.dialog = document.getElementById("account-dialog");
    if (!this.dialog) return;

    this.setupEventListeners();
    this.render();
  }

  private setupEventListeners(): void {
    if (!this.dialog) return;

    // Dialog open event - refresh content when dialog opens
    this.dialog.addEventListener("dialog:open", async () => {
      await this.render();
      // Load orders if orders tab is active
      if (this.currentTab === "orders") {
        requestAnimationFrame(() => {
          this.loadOrders();
        });
      }
    });

    // Language changes
    this.i18nManager.onLanguageChange(() => {
      this.render();
    });

    // Tab switching
    this.dialog.addEventListener("click", (e) => {
      const target = e.target as HTMLElement;
      if (target.classList.contains("account-tab")) {
        e.preventDefault();
        const tab = target.getAttribute("data-tab");
        if (tab) {
          this.switchTab(tab);
        }
      }
    });

    // Profile form submission
    this.dialog.addEventListener("submit", (e) => {
      const target = e.target as HTMLFormElement;
      if (target.id === "profile-form") {
        e.preventDefault();
        this.updateProfile(target);
      }
    });

    // GDPR actions
    this.dialog.addEventListener("click", (e) => {
      const target = e.target as HTMLElement;
      if (target.id === "export-data-btn") {
        e.preventDefault();
        this.exportUserData();
      } else if (target.id === "delete-account-btn") {
        e.preventDefault();
        this.showDeleteConfirmation();
      }
    });

    // Delete confirmation
    this.dialog.addEventListener("click", (e) => {
      const target = e.target as HTMLElement;
      if (target.id === "confirm-delete-btn") {
        e.preventDefault();
        this.deleteAccount();
      } else if (target.id === "cancel-delete-btn") {
        e.preventDefault();
        this.hideDeleteConfirmation();
      }
    });

    // Logout
    this.dialog.addEventListener("click", (e) => {
      const target = e.target as HTMLElement;
      if (target.id === "logout-btn") {
        e.preventDefault();
        this.logout();
      }
    });
  }

  private async render(): Promise<void> {
    if (!this.dialog) return;

    const user = this.authManager.getCurrentUser();
    if (!user) {
      this.dialogManager.closeDialog();
      return;
    }

    const content = this.dialog.querySelector("#account-content");
    if (!content) return;

    // Fetch customer data from backend
    let customerData: any = null;
    try {
      const token = await this.authManager.getIdToken();
      const response = await fetch("/api/account", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.ok) {
        customerData = await response.json();
      }
    } catch (error) {
      console.error("Failed to load customer data:", error);
    }

    // Use Firebase user data as fallback
    const userProfile = this.authManager.getUserProfile();
    const userData = {
      name: customerData?.name || userProfile?.name || user.email || "",
      email: userProfile?.email || user.email || "",
      phone: customerData?.phone || "",
      address: customerData?.address?.line1 || customerData?.address || "",
      city: customerData?.address?.city || customerData?.city || "",
      postal_code:
        customerData?.address?.postal_code || customerData?.postal_code || "",
      country: customerData?.address?.country || customerData?.country || "",
    };

    content.innerHTML = `
      <div class="account-tabs">
        <button class="account-tab ${
          this.currentTab === "profile" ? "active" : ""
        }" data-tab="profile">
          ${this.i18nManager.t("account.profile")}
        </button>
        <button class="account-tab ${
          this.currentTab === "orders" ? "active" : ""
        }" data-tab="orders">
          ${this.i18nManager.t("account.orders")}
        </button>
        <button class="account-tab ${
          this.currentTab === "settings" ? "active" : ""
        }" data-tab="settings">
          ${this.i18nManager.t("account.settings")}
        </button>
      </div>

      <div class="account-content-tabs">
        ${this.renderProfileTab(userData)}
        ${this.renderOrdersTab()}
        ${this.renderSettingsTab()}
      </div>
    `;

    this.showCurrentTab();
  }

  private renderProfileTab(user: any): string {
    // Extract address fields properly
    const address =
      typeof user.address === "string"
        ? user.address
        : user.address?.line1 || "";
    const city = user.city || user.address?.city || "";
    const postalCode = user.postal_code || user.address?.postal_code || "";
    const country = user.country || user.address?.country || "";

    return `
      <div class="account-tab-content ${
        this.currentTab === "profile" ? "active" : ""
      }" data-tab-content="profile">
        <div class="profile-section">
          <h3>${this.i18nManager.t("account.profile")}</h3>
          
          <form id="profile-form" class="profile-form">
            <div class="form-group">
              <label for="profile-name">${this.i18nManager.t(
                "account.name"
              )}</label>
              <input type="text" id="profile-name" name="name" autocomplete="name" value="${
                user.name || ""
              }" required>
            </div>
            
            <div class="form-group">
              <label for="profile-email">${this.i18nManager.t(
                "account.email"
              )}</label>
              <input type="email" id="profile-email" name="email" autocomplete="email" value="${
                user.email || ""
              }" readonly>
              <small class="form-help">${this.i18nManager.t(
                "account.email_readonly"
              )}</small>
            </div>
            
            <div class="form-group">
              <label for="profile-address">${this.i18nManager.t(
                "account.address"
              )}</label>
              <input type="text" id="profile-address" name="address" autocomplete="street-address" value="${address}">
            </div>
            
            <div class="form-row">
            <div class="form-group">
              <label for="profile-city">${this.i18nManager.t(
                "account.city"
              )}</label>
                <input type="text" id="profile-city" name="city" autocomplete="address-level2" value="${city}">
            </div>
            
            <div class="form-group">
              <label for="profile-postal-code">${this.i18nManager.t(
                "account.postal_code"
              )}</label>
                <input type="text" id="profile-postal-code" name="postal_code" autocomplete="postal-code" value="${postalCode}">
              </div>
            </div>
            
            <div class="form-group">
              <label for="profile-country">${this.i18nManager.t(
                "account.country"
              )}</label>
              <select id="profile-country" name="country" autocomplete="country">
                <option value="SE" ${
                  country === "SE" ? "selected" : ""
                }>Sweden</option>
                <option value="NO" ${
                  country === "NO" ? "selected" : ""
                }>Norway</option>
                <option value="DK" ${
                  country === "DK" ? "selected" : ""
                }>Denmark</option>
                <option value="FI" ${
                  country === "FI" ? "selected" : ""
                }>Finland</option>
                <option value="DE" ${
                  country === "DE" ? "selected" : ""
                }>Germany</option>
                <option value="US" ${
                  country === "US" ? "selected" : ""
                }>United States</option>
                <option value="GB" ${
                  country === "GB" ? "selected" : ""
                }>United Kingdom</option>
              </select>
            </div>
            
            <div class="form-actions">
              <button type="submit" class="glass-button glass-button--primary">
                ${this.i18nManager.t("account.save_changes")}
              </button>
            </div>
          </form>
        </div>
      </div>
    `;
  }

  private renderOrdersTab(): string {
    return `
      <div class="account-tab-content ${
        this.currentTab === "orders" ? "active" : ""
      }" data-tab-content="orders">
        <div class="orders-section">
          <h3>${this.i18nManager.t("orders.title")}</h3>
          
          <div class="orders-list" id="orders-list">
            <div class="loading-spinner"></div>
            <p>${this.i18nManager.t("common.loading")}</p>
          </div>
        </div>
      </div>
    `;
  }

  private renderSettingsTab(): string {
    return `
      <div class="account-tab-content ${
        this.currentTab === "settings" ? "active" : ""
      }" data-tab-content="settings">
        <div class="settings-section">
          <h3>${this.i18nManager.t("account.settings")}</h3>
          
          <div class="settings-group">
            <h4>${this.i18nManager.t("gdpr.title")}</h4>
            <p class="settings-description">
              ${this.i18nManager.t("gdpr.description")}
            </p>
            
            <div class="settings-actions">
              <button id="export-data-btn" class="glass-button">
                ${this.i18nManager.t("gdpr.data_export")}
              </button>
              
              <button id="delete-account-btn" class="glass-button glass-button--danger">
                ${this.i18nManager.t("gdpr.delete_account")}
              </button>
            </div>
          </div>
          
          <div class="settings-group">
            <h4>${this.i18nManager.t("account.logout")}</h4>
            <p class="settings-description">
              ${this.i18nManager.t("account.logout_description")}
            </p>
            
            <button id="logout-btn" class="glass-button">
              ${this.i18nManager.t("account.logout")}
            </button>
          </div>
        </div>
        
        <!-- Delete Confirmation Modal -->
        <div class="delete-confirmation hidden" id="delete-confirmation">
          <div class="confirmation-content">
            <h3>${this.i18nManager.t("gdpr.confirm_delete")}</h3>
            <p class="delete-warning-text">${this.i18nManager.t("gdpr.delete_warning")}</p>
            <p class="delete-cannot-undo"><em>${this.i18nManager.t("gdpr.cannot_be_undone")}</em></p>
            
            <div class="confirmation-actions">
              <button id="cancel-delete-btn" class="glass-button">
                ${this.i18nManager.t("common.cancel")}
              </button>
              <button id="confirm-delete-btn" class="glass-button glass-button--danger">
                ${this.i18nManager.t("common.confirm")}
              </button>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  private async switchTab(tab: string): Promise<void> {
    this.currentTab = tab;
    await this.render();
    this.showCurrentTab();

    // Load data for specific tabs - wait for DOM to update
    if (tab === "orders") {
      // Use requestAnimationFrame to ensure DOM is updated before loading
      requestAnimationFrame(() => {
        this.loadOrders();
      });
    }
  }

  private showCurrentTab(): void {
    if (!this.dialog) return;

    // Update tab buttons
    const tabs = this.dialog.querySelectorAll(".account-tab");
    tabs.forEach((tab) => {
      tab.classList.remove("active");
      if (tab.getAttribute("data-tab") === this.currentTab) {
        tab.classList.add("active");
      }
    });

    // Show/hide tab content
    const tabContents = this.dialog.querySelectorAll(".account-tab-content");
    tabContents.forEach((content) => {
      content.classList.remove("active");
      if (content.getAttribute("data-tab-content") === this.currentTab) {
        content.classList.add("active");
      }
    });
  }

  private async updateProfile(form: HTMLFormElement): Promise<void> {
    const formData = new FormData(form);
    const updates = {
      name: formData.get("name") as string,
      email: formData.get("email") as string,
      address: {
        line1: formData.get("address") as string,
      city: formData.get("city") as string,
      postal_code: formData.get("postal_code") as string,
      country: formData.get("country") as string,
      },
    };

    try {
      const token = await this.authManager.getIdToken();
      const response = await fetch("/api/account", {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(updates),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

        this.showSuccess(this.i18nManager.t("success.profile_updated"));
      await this.render(); // Refresh the form
    } catch (error) {
      console.error("Failed to update profile:", error);
      this.showError(this.i18nManager.t("error.update_failed"));
    }
  }

  private async loadOrders(): Promise<void> {
    // Wait a tick to ensure DOM is ready after render
    await new Promise(resolve => requestAnimationFrame(resolve));
    
    const ordersList = this.dialog?.querySelector("#orders-list");
    if (!ordersList) {
      console.error("Orders list element not found, retrying...");
      // Retry once after a short delay if element not found
      setTimeout(() => {
        const retryList = this.dialog?.querySelector("#orders-list");
        if (retryList) {
          this.loadOrders();
        } else {
          console.error("Orders list element still not found after retry");
        }
      }, 100);
      return;
    }

    // Show loading state
    ordersList.innerHTML = `
      <div class="loading-state">
        <p>${this.i18nManager.t("common.loading") || "Loading..."}</p>
      </div>
    `;

    try {
      const token = await this.authManager.getIdToken();
      if (!token) {
        throw new Error("Not authenticated");
      }

      const response = await fetch("/api/orders", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Orders API error:", response.status, errorText);
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const orders = await response.json();
      
      this.renderOrders(orders);
    } catch (error) {
      console.error("Failed to load orders:", error);
      ordersList.innerHTML = `
        <div class="error-message">
          <p>${this.i18nManager.t("error.load_orders") || "Failed to load orders"}</p>
          <p style="font-size: 0.875rem; color: var(--color-text-muted); margin-top: var(--space-sm);">
            ${error instanceof Error ? error.message : String(error)}
          </p>
        </div>
      `;
    }
  }

  private renderOrders(orders: any[]): void {
    const ordersList = this.dialog?.querySelector("#orders-list");
    if (!ordersList) {
      console.error("Orders list element not found in renderOrders");
      return;
    }

    if (!orders || !Array.isArray(orders) || orders.length === 0) {
      ordersList.innerHTML = `
        <div class="empty-state">
          <p>${this.i18nManager.t("orders.no_orders") || "No orders found"}</p>
        </div>
      `;
      return;
    }

    try {
      // Sort orders by date (newest first)
      const sortedOrders = [...orders].sort((a, b) => {
        const dateA = a.created_at || 0;
        const dateB = b.created_at || 0;
        return dateB - dateA;
      });


      ordersList.innerHTML = sortedOrders
        .map((order) => {
          try {
            const statusClass = `order-status--${order.status || "unknown"}`;
            const statusText = this.i18nManager.t(`orders.status.${order.status}`) || order.status || "Unknown";
            
            // Format date - created_at is a Unix timestamp in seconds
            let orderDate = "Unknown date";
            if (order.created_at) {
              try {
                // Convert Unix timestamp (seconds) to Date
                const date = new Date(order.created_at * 1000);
                if (isNaN(date.getTime())) {
                  orderDate = "Invalid date";
                } else {
                  try {
                    orderDate = this.i18nManager.formatDate(date);
                  } catch (e) {
                    console.error("Error in formatDate:", e);
                    orderDate = date.toLocaleDateString();
                  }
                }
              } catch (e) {
                console.error("Error formatting date:", e, order.created_at);
                orderDate = "Invalid date";
              }
            }

            // Format order ID to show last 8 characters
            const shortOrderId = order.id ? order.id.substring(0, 8) : "Unknown";

            return `
              <div class="order-item">
                <div class="order-header">
                  <div class="order-info">
                    <h4>Order #${shortOrderId}</h4>
                    <p class="order-date">${orderDate}</p>
                  </div>
                  <div class="order-status ${statusClass}">
                    ${statusText}
                  </div>
                </div>
                
                <div class="order-details">
                  <div class="order-items">
                    ${(order.items || [])
                      .map(
                        (item: any) => {
                          const itemPrice = item.price_sek || item.price || 0;
                          const itemTotal = itemPrice * (item.quantity || 1);
                          const priceText = itemTotal > 0 
                            ? this.i18nManager.formatCurrency(itemTotal)
                            : this.i18nManager.t("orders.price_not_available") || "N/A";
                          const itemName = item.name || `Product ${item.product_id || "Unknown"}`;
                          
                          // Render options if they exist
                          let optionsHTML = "";
                          if (item.options && typeof item.options === "object" && Object.keys(item.options).length > 0) {
                            const optionsList = Object.entries(item.options)
                              .map(([key, value]) => {
                                const optionKey = this.i18nManager.t(`product.options.${key}`) || key;
                                return `<span class="item-option">${optionKey}: ${value}</span>`;
                              })
                              .join(", ");
                            optionsHTML = `<div class="item-options" style="font-size: 0.875rem; color: var(--color-text-secondary); margin-top: 4px;">${optionsList}</div>`;
                          }
                          
                          return `
                            <div class="order-item-detail">
                              <div class="item-info">
                                <span class="item-name">${itemName}</span>
                                <span class="item-quantity">x${item.quantity || 1}</span>
                                <span class="item-price">${priceText}</span>
                              </div>
                              ${optionsHTML}
                            </div>
                          `;
                        }
                      )
                      .join("")}
                  </div>
                  
                  <div class="order-total">
                    <strong>${this.i18nManager.t(
                      "orders.order_total"
                    ) || "Total"}: ${this.i18nManager.formatCurrency(order.total_sek || 0)}</strong>
                  </div>
                  
                  ${
                    order.tracking_number
                      ? `
                    <div class="order-tracking">
                      <p><strong>${this.i18nManager.t(
                        "orders.tracking_number"
                      ) || "Tracking"}:</strong> ${order.tracking_number}</p>
                    </div>
                  `
                      : ""
                  }
                </div>
              </div>
            `;
          } catch (error) {
            console.error("Error rendering order:", error, order);
            return `
              <div class="order-item">
                <div class="order-header">
                  <div class="order-info">
                    <h4>Order #${order.id || "Unknown"}</h4>
                    <p class="order-date">Error loading order</p>
                  </div>
                </div>
              </div>
            `;
          }
        })
        .join("");
      
    } catch (error) {
      console.error("Error rendering orders:", error);
      ordersList.innerHTML = `
        <div class="error-message">
          <p>${this.i18nManager.t("error.load_orders") || "Failed to display orders"}</p>
          <p style="font-size: 0.875rem; color: var(--color-text-muted); margin-top: var(--space-sm);">
            ${error instanceof Error ? error.message : String(error)}
          </p>
        </div>
      `;
    }
  }

  private async exportUserData(): Promise<void> {
    try {
      const response = await fetch("/api/account/export-data", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${await this.authManager.getIdToken()}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `user-data-${Date.now()}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      this.showSuccess(this.i18nManager.t("gdpr.data_exported"));
    } catch (error) {
      console.error("Failed to export user data:", error);
      this.showError(this.i18nManager.t("error.export_failed"));
    }
  }

  private showDeleteConfirmation(): void {
    const confirmation = this.dialog?.querySelector("#delete-confirmation");
    if (confirmation) {
      confirmation.classList.remove("hidden");
    }
  }

  private hideDeleteConfirmation(): void {
    const confirmation = this.dialog?.querySelector("#delete-confirmation");
    if (confirmation) {
      confirmation.classList.add("hidden");
    }
  }

  private async deleteAccount(): Promise<void> {
    try {
      const response = await fetch("/api/account", {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${await this.authManager.getIdToken()}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      this.showSuccess(this.i18nManager.t("gdpr.account_deleted"));
      this.hideDeleteConfirmation();

      // Close dialog and redirect to home
      setTimeout(() => {
        this.dialogManager.closeDialog();
        window.location.reload();
      }, 2000);
    } catch (error) {
      console.error("Failed to delete account:", error);
      this.showError(this.i18nManager.t("error.delete_failed"));
    }
  }

  private async logout(): Promise<void> {
    try {
      await this.authManager.signOut();
      this.showSuccess(this.i18nManager.t("auth.logout_success"));

      // Close dialog and redirect to home
      setTimeout(() => {
        this.dialogManager.closeDialog();
        window.location.reload();
      }, 1000);
    } catch (error) {
      console.error("Failed to logout:", error);
      this.showError(this.i18nManager.t("error.logout_failed"));
    }
  }

  private showSuccess(message: string): void {
    this.showNotification(message, "success");
  }

  private showError(message: string): void {
    this.showNotification(message, "error");
  }

  private showNotification(message: string, type: "success" | "error"): void {
    const notification = document.createElement("div");
    notification.className = `notification notification--${type}`;
    notification.textContent = message;

    document.body.appendChild(notification);

    requestAnimationFrame(() => {
      notification.classList.add("show");
    });

    setTimeout(() => {
      notification.classList.remove("show");
      setTimeout(() => {
        document.body.removeChild(notification);
      }, 300);
    }, 3000);
  }
}
